import { useState } from 'react'
import { AppBar, Box, Drawer, IconButton, ListItemText, MenuItem, MenuList, Toolbar, useMediaQuery, useTheme } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { HomeOutlined, DeviceThermostatTwoTone, SettingsOutlined, Login, ChevronLeft, BugReportTwoTone, AppRegistration, Close, PersonSearchTwoTone, PersonTwoTone, SmartButtonOutlined, GridViewOutlined, Equalizer, CropPortrait, ElectricalServices, Groups, TroubleshootTwoTone, DirectionsRun, EuroOutlined, NotificationsOff, LockReset, ContactMailTwoTone, ForwardToInboxTwoTone, PaymentsTwoTone, HandshakeTwoTone } from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'
import { callAxios } from './hooks/useAxios'
import { useLocation, useNavigate } from 'react-router-dom'
import { useApp } from './hooks/useApp'
import { SideMenuIcon, menuItemSx, menuTextSx } from './components/SideMenuIcon'
import { hasCIEnabled } from './utils/time'
import { useDevices } from './hooks/useDevices'
import { LanguageSelector } from './components/LanguageSelector'
import { isNavState } from './types/models'
import { Appliance } from './classes/appliances/Appliance'
import { Logo } from './components/Logo'
import { Safety } from './classes/Safety'

export function EnionMenu(): React.ReactElement {

  const { t } = useTranslation()

  // Definition of special pages that has only X to cancel and go back.
  const SUB_PAGES: {[key:string]: string | number} = {
    '\\/claim': '/devices',
    '\\/config\\/\\d+$': '/devices',
    '\\/config\\/\\d+\\/\\d+': '/',
    '\\/admin\\/fcr\\/(\\d+)$': '/admin/devices/$1',
    '\\/radio\\/\\d+$': '/devices',
    '\\/rules\\/(\\d+)$': '/config/$1',
    '\\/rules\\/(\\d+)/(\\d+)$': '/config/$1/$2',
    '\\/admin\\/users\\/\\d+$': '/admin/users',
    '\\/admin\\/devices\\/\\d+$': '/admin/devices',
    '\\/appliances\\/new': '/appliances',
    '\\/appliances\\/new\\/[A-Z_]+': '/appliances/new',
    '\\/appliances\\/new\\/([A-Z_]+)\\/[A-Z_]+': '/appliances/new/$1',
    '\\/appliances\\/new\\/([A-Z_]+)\\/([A-Z_]+)\\/[0-9]+': '/appliances/new/$1/$2',
    '\\/appliances\\/\\d+$': '/appliances',
    '\\/appliances\\/test\\/\\d+$': 'NONE',
  }

  // Persistent drawer only when it can't overlap the centred content: 2 × 250 px drawer + 600 px (Container sm)
  const isWide = useMediaQuery('(min-width:1100px)')
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(isWide)

  const app = useApp()
  const { setDevices, control } = useDevices()
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  let returnUrl: string | number = ''

  for (const path of Object.keys(SUB_PAGES)) {
    const regex = new RegExp(path)
    const matches = regex.exec(location.pathname)
    if (matches) {
      returnUrl = SUB_PAGES[path]
      if (typeof returnUrl === 'number') {
        navigate(returnUrl)
      } else {
        for (let i = 1; matches[i]; i++) {
          returnUrl = returnUrl.replace(`$${i}`, matches[i])
        }
      }
    }
  }

  const isSubPage = !!returnUrl
  const noMenu = (returnUrl === 'NONE')

  const onLogout = () => {
    callAxios({
      method: 'POST',
      url: '/auth/logout',
    }).then(res => {
      if (!res.success) {
        app.addError(res)
      }
      app.setUser(null)
      app.setLocations([])
      app.setInstallerLevel(false)
      setDevices([])
      Safety.clear()
      Appliance.clearConfig()
      navigate('/')
    })
  }

  const onDrawerOpen = (): void => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  const onDrawerClose = (): void => {
    setIsDrawerOpen(false)
  }

  const onNavigate = (url: string): void => {
    !isWide && onDrawerClose()
    navigate(url, { state: { prev: location.pathname } })
  }

  const isActive = (url: string): boolean =>
    location.pathname === url || location.pathname.startsWith(`${url}/`)

  const isCustomer = !!app.user && app.user.role !== 'ROOT' && app.user.role !== 'ADMIN'

  const onCancel = async (): Promise<void> => {
    let prevUrl
    control.clearPendingConfig()

    if (isNavState(location.state)) {
      if ('prev' in location.state) {
        prevUrl = location.state.prev
      }
      if ('disable' in location.state) {
        // The port may no longer be loaded, e.g. the history entry outlived the session.
        // No redraw from here: going back in the history below takes effect only later,
        // and the page being left would be drawn once more with its pending config gone.
        control.findPortControl(location.state.disable.port)?.setConfig('enabled', false, false)
      }
    }

    if (prevUrl) {
      navigate(prevUrl)
    } else if (window.history?.state?.idx > 0) {
      navigate(-1)
    } else {
      navigate(returnUrl as string)
    }
  }

  return (
    <AppBar position="static" sx={{ boxShadow: 0 }} data-cy="app-bar">
      <Toolbar disableGutters={false} sx={{
        display: 'grid',
        px: '0 !important',
        gridTemplateColumns: '1fr 1fr 1fr',
        color: theme.palette.grey[100],
        backgroundColor: hasCIEnabled() ? 'error.main' : theme.palette.background.paper,
      }}>
        <Box sx={{ textAlign: 'left' }}>
          {!isSubPage &&
            <IconButton
              size="large"
              edge="start"
              aria-label="menu"
              onClick={onDrawerOpen}
              >
              <MenuIcon sx={{ fontSize: '2rem' }} />
            </IconButton>
          }

        </Box>
        <Box sx={{ textAlign: 'center' }}>{!isSubPage && <Logo/>}</Box>
        <Box sx={{ textAlign: 'right' }}>
          {isSubPage && <>
              <IconButton
                size="large"
                edge="end"
                aria-label="cancel"
                onClick={() => onCancel()}
                disabled={noMenu}
              >
                <Close sx={{ fontSize: '2rem', color: noMenu ? 'transparent' : 'inherit' }} />
              </IconButton>
            </>
          }
          {!isSubPage && <LanguageSelector/>}
        </Box>
      </Toolbar>

      {
        <Drawer
          // Phones: a modal drawer that closes on a tap outside it. Desktop: stays docked.
          variant={isWide ? 'persistent' : 'temporary'}
          anchor="left"
          open={isDrawerOpen}
          onClose={onDrawerClose}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: { xs: 270, md: 250 },
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.palette.grey[900],
              backgroundImage: 'none', // no dark-mode elevation tint on the modal drawer
              borderRight: 'none',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1, pt: 2, pb: 1 }}>
            <IconButton aria-label={t('Close')} data-cy="menu-close" onClick={onDrawerClose} sx={{ color: 'grey.500' }}>
              <ChevronLeft />
            </IconButton>
          </Box>
          <MenuList data-cy="menu">
            {isCustomer &&
              <SideMenuIcon data-cy="menu-Home" title={t('Menu-Home')} url="/home" icon={HomeOutlined} handleNavigate={onNavigate} active={isActive('/home')} />
            }
            {isCustomer &&
              <SideMenuIcon data-cy="menu-Devices" title={t('Menu-Enion Devices')} url="/devices" icon={CropPortrait} handleNavigate={onNavigate} active={isActive('/devices')} />
            }
            {isCustomer &&
              <SideMenuIcon data-cy="menu-Appliances" title={t('Menu-Appliances')} url="/appliances" icon={ElectricalServices} handleNavigate={onNavigate} active={isActive('/appliances')} />
            }
            {isCustomer && app.userHas('HISTORY_PAGE') &&
              <SideMenuIcon data-cy="menu-History" title={t('Menu-History')} url="/history" icon={Equalizer} handleNavigate={onNavigate} active={isActive('/history')} />
            }
            {isCustomer && app.userHas('FCR_BATTERIES') &&
              <SideMenuIcon data-cy="menu-Profits" title={t('Menu-Profits')} url="/profits" icon={EuroOutlined} handleNavigate={onNavigate} active={isActive('/profits')} />
            }
            {isCustomer && app.userHas('WIDGET_PAGE') &&
              <SideMenuIcon data-cy="menu-Widgets" title={t('Menu-Widgets')} url="/widgets" icon={GridViewOutlined} handleNavigate={onNavigate} active={isActive('/widgets')} />
            }
            {isCustomer &&
              <SideMenuIcon data-cy="menu-Settings" title={t('Menu-Settings')} url="/settings" icon={SettingsOutlined} handleNavigate={onNavigate} active={isActive('/settings')} />
            }
            {app.userHas('DEBUG_PAGE') &&
              <SideMenuIcon data-cy="menu-Debug" title={t('Menu-Debug')} url="/debug" icon={BugReportTwoTone} handleNavigate={onNavigate} active={isActive('/debug')} />
            }
            {app.userHas('CI_CONTROL') &&
              <SideMenuIcon data-cy="menu-CI Control" title={t('Menu-CI Control')} url="/ci" icon={SmartButtonOutlined} handleNavigate={onNavigate} active={isActive('/ci')} />
            }
            {!app.user &&
              <SideMenuIcon data-cy="menu-Log In" title={t('Menu-Login')} url="/login" icon={Login} handleNavigate={onNavigate} active={isActive('/login')} />
            }
            {!app.user &&
              <SideMenuIcon data-cy="menu-Register" title={t('Menu-Register')} url="/register" icon={AppRegistration} handleNavigate={onNavigate} active={isActive('/register')} />
            }
            {!app.user &&
              <SideMenuIcon data-cy="menu-Forgot Password" title={t('Menu-Forgot Password')} url="/forgot-password" icon={LockReset} handleNavigate={onNavigate} active={isActive('/forgot-password')} />
            }
          </MenuList>
          {
            app.user?.role === 'ROOT' &&
            <>
              <p className="menu-title-element"><Trans>Admin</Trans></p>
              <MenuList>
                <SideMenuIcon data-cy="menu-Quick Finder" title={t('Menu-Quick Finder')} url="/admin/quick-finder" icon={PersonSearchTwoTone} handleNavigate={onNavigate} active={isActive('/admin/quick-finder')} />
                <SideMenuIcon data-cy="menu-Device Admin" title={t('Menu-Device Admin')} url="/admin/devices" icon={DeviceThermostatTwoTone} handleNavigate={onNavigate} active={isActive('/admin/devices')} />
                <SideMenuIcon data-cy="menu-User Admin" title={t('Menu-User Admin')} url="/admin/users" icon={PersonTwoTone} handleNavigate={onNavigate} active={isActive('/admin/users')} />
                <SideMenuIcon data-cy="menu-Group Admin" title={t('Menu-Group Admin')} url="/admin/user-groups" icon={Groups} handleNavigate={onNavigate} active={isActive('/admin/user-groups')} />
                <SideMenuIcon data-cy="menu-Telemetry" title={t('Menu-Telemetry')} url="/admin/telemetry" icon={TroubleshootTwoTone} handleNavigate={onNavigate} active={isActive('/admin/telemetry')} />
                <SideMenuIcon data-cy="menu-DevOps" title={t('Menu-DevOps')} url="/admin/devops" icon={DirectionsRun} handleNavigate={onNavigate} active={isActive('/admin/devops')} />
                <SideMenuIcon data-cy="menu-Alert Whitelist" title={t('Menu-Alert Whitelist')} url="/admin/alert-whitelist" icon={NotificationsOff} handleNavigate={onNavigate} active={isActive('/admin/alert-whitelist')} />
                <SideMenuIcon data-cy="menu-Billing" title={t('Menu-Billing')} url="/admin/billing" icon={DirectionsRun} handleNavigate={onNavigate} active={isActive('/admin/billing')} />
                <SideMenuIcon data-cy="menu-BRPs" title={t('Menu-BRPs')} url="/admin/brps" icon={ContactMailTwoTone} handleNavigate={onNavigate} active={isActive('/admin/brps')} />
                <SideMenuIcon data-cy="menu-BRP Notifications" title={t('Menu-BRP Notifications')} url="/admin/brp-notifications" icon={ForwardToInboxTwoTone} handleNavigate={onNavigate} active={isActive('/admin/brp-notifications')} />
                <SideMenuIcon data-cy="menu-Reserve Payouts" title={t('Menu-Reserve Payouts')} url="/admin/reserve-payouts" icon={PaymentsTwoTone} handleNavigate={onNavigate} active={isActive('/admin/reserve-payouts')} />
                <SideMenuIcon data-cy="menu-Payout Partners" title={t('Menu-Payout Partners')} url="/admin/payout-partners" icon={HandshakeTwoTone} handleNavigate={onNavigate} active={isActive('/admin/payout-partners')} />
              </MenuList>
            </>
          }
          {
            app.user && app.user?.role === 'ADMIN' &&
            <>
              <p className="menu-title-element"><Trans>Admin</Trans></p>
              <MenuList>
                <SideMenuIcon data-cy="menu-Group Admin" title={t('Menu-Group Admin')} url="/admin/user-groups" icon={Groups} handleNavigate={onNavigate} active={isActive('/admin/user-groups')} />
              </MenuList>
            </>
          }
          {app.user &&
            <>
              <MenuList>
                <MenuItem data-cy="menu-Log Out" onClick={() => onLogout()} sx={menuItemSx}>
                  <ListItemText primaryTypographyProps={{ sx: menuTextSx(false) }}>{t('Menu-Logout')}</ListItemText>
                </MenuItem>
              </MenuList>
            </>
          }
          <Box data-cy="menu-footer" sx={{ mt: 'auto', px: 3, pt: 4, pb: 3, color: 'grey.600', fontSize: '0.9rem', lineHeight: 1.8 }}>
            {app.user && <div>{app.user.email}</div>}
          </Box>
        </Drawer>
      }
    </AppBar>
  )
}
